import React from 'react';
import { formatCurrencyWhole, formatReportCell } from '../../utils/formatCurrency.js';

const MONEY_KEYS = ['sub_total', 'sgst', 'cgst', 'igst', 'total'];

function money(value) {
  return formatCurrencyWhole(value);
}

function MoneyCells({ row }) {
  return MONEY_KEYS.map((key) => (
    <td key={key} className="px-3 py-2 text-right tabular-nums whitespace-nowrap border-b border-slate-200">
      {money(row[key])}
    </td>
  ));
}

function StateBlock({ block, gstinLabel }) {
  return (
    <div className="mb-4 last:mb-0">
      <p className="text-sm font-bold text-slate-800 m-0">{block.title}</p>
      {gstinLabel && (
        <p className="text-[11px] text-slate-500 mt-1 mb-0">{gstinLabel}</p>
      )}
      <div className="overflow-x-auto mt-2 rounded-lg border border-slate-200">
        <table className="w-full border-collapse text-xs min-w-[420px]">
          <thead>
            <tr style={{ background: '#0f766e', color: '#fff' }}>
              {['Sub Total', 'SGST', 'CGST', 'IGST', 'Total'].map((label) => (
                <th key={label} className="px-3 py-2 text-right font-semibold whitespace-nowrap">
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="bg-white">
              <MoneyCells row={block} />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DetailTable({ columns, rows, recordsLoading }) {
  if (recordsLoading && !rows.length) {
    return <p className="text-sm text-slate-400 text-center py-6 m-0">Loading records…</p>;
  }
  if (!rows.length) {
    return <p className="text-sm text-slate-400 text-center py-6 m-0">No consignments for the selected filters.</p>;
  }
  return (
    <>
      <div className="md:hidden p-2.5 space-y-2">
        {rows.map((row, idx) => {
          const cells = Array.isArray(row) ? row : columns.map((col) => row[col]);
          return (
            <div key={idx} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <div className="text-xs font-bold text-slate-800 mb-2">{cells[1] || cells[0] || `#${idx + 1}`}</div>
              <div className="grid grid-cols-1 gap-1.5">
                {columns.map((col, cidx) => (
                  <div key={col} className="flex justify-between gap-3 text-[11px]">
                    <span className="text-slate-500 font-semibold shrink-0 max-w-[45%]">{col}</span>
                    <span className="text-slate-800 text-right break-all">
                      {cells[cidx] == null || cells[cidx] === '' ? '—' : formatReportCell(col, cells[cidx], '—')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full border-collapse text-xs min-w-[960px]">
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col}
                  className="text-left px-3 py-2.5 whitespace-nowrap font-bold border-b border-sky-300"
                  style={{ background: '#B4C7E7', color: '#1F4E79' }}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const cells = Array.isArray(row) ? row : columns.map((col) => row[col]);
              return (
                <tr key={idx} style={{ background: idx % 2 ? '#F2F7FF' : '#fff' }}>
                  {cells.map((cell, cidx) => (
                    <td key={cidx} className="px-3 py-2 border-b border-slate-200 whitespace-nowrap">
                      {cell == null || cell === '' ? '' : formatReportCell(columns[cidx], cell, '')}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

const GstCollectionReportView = ({ payload, loading, recordsLoading = false }) => {
  const gst = payload?.meta?.gst;
  const columns = payload?.columns || [];
  const rows = payload?.rows || [];

  if (loading && !gst) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-sm text-slate-400">
        Loading…
      </div>
    );
  }

  if (!gst) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-sm text-slate-400">
        No data for the selected filters.
      </div>
    );
  }

  const groups = gst.state_groups || [];
  const summary = gst.summary || [];

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 text-[11px] sm:text-xs text-slate-500">
        <span>Report Date : {gst.from_date} to {gst.to_date}</span>
        <span className="sm:text-right">
          Report At : {gst.generated_at}
          {gst.generated_by ? ` · Report By : ${gst.generated_by}` : ''}
        </span>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        <section className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          <div className="px-3 py-2 text-xs font-bold tracking-wide text-slate-600 border-b border-slate-200">
            STATE WISE
          </div>
          <div className="p-3">
            {groups.length === 0 && (
              <p className="text-sm text-slate-400 text-center py-6 m-0">No data for the selected filters.</p>
            )}
            {groups.map((group) => (
              <div key={`${group.state}-${group.gstin}`}>
                {(group.blocks || []).map((block) => (
                  <StateBlock key={block.key} block={block} gstinLabel={group.label} />
                ))}
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          <div className="px-3 py-2 text-sm font-bold text-slate-800 border-b border-slate-200">
            Summary
          </div>
          <div className="p-3 overflow-x-auto">
            <table className="w-full border-collapse text-xs min-w-[520px]">
              <thead>
                <tr style={{ background: '#0f766e', color: '#fff' }}>
                  <th className="px-3 py-2 text-left font-semibold">Transaction</th>
                  <th className="px-3 py-2 text-right font-semibold">Sub Total (A)</th>
                  <th className="px-3 py-2 text-right font-semibold">SGST (B)</th>
                  <th className="px-3 py-2 text-right font-semibold">CGST (C)</th>
                  <th className="px-3 py-2 text-right font-semibold">IGST (D)</th>
                  <th className="px-3 py-2 text-right font-semibold">Total (A+B+C+D)</th>
                </tr>
              </thead>
              <tbody>
                {summary.map((row) => (
                  <tr
                    key={row.key || row.label}
                    style={{
                      background: row.highlight ? '#e2e8f0' : '#fff',
                      fontWeight: row.highlight ? 700 : 400,
                    }}
                  >
                    <td className="px-3 py-2 border-b border-slate-200 whitespace-nowrap">{row.label}</td>
                    <MoneyCells row={row} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {gst.list_on_screen ? (
        <section className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          <div className="px-3 sm:px-4 py-2.5 border-b border-slate-200 flex justify-between items-center gap-2">
            <span className="text-xs sm:text-[13px] font-bold text-slate-900">
              {gst.collection_type === 'transaction' ? 'Transactions' : 'Booking Transactions'}
            </span>
            <span className="text-[11px] text-slate-500">
              {recordsLoading && !rows.length ? 'Loading records…' : `${rows.length}${payload?.pagination?.total != null ? ` of ${payload.pagination.total}` : ''} record(s)`}
            </span>
          </div>
          <DetailTable columns={columns} rows={rows} recordsLoading={recordsLoading} />
        </section>
      ) : (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {payload?.meta?.warning
            || 'This date range is above 7 days. The consignment list is in the Excel file. State-wise totals and the summary stay on screen.'}
        </div>
      )}
    </div>
  );
};

export default GstCollectionReportView;
